import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, noop } from 'lodash';
import { translate } from '@fiverr-private/i18n-react';
import { StatusToast } from '@fiverr-private/orca';
import { STEPS } from '../../constants';
import { CLICKED_ON_X_BUTTON, FIRST_MESSAGE_POPUP_IS_SHOWN } from '../../constants/biEvents';
import { useAttachments } from '../../context/contexts/attachments';
import { useMessage } from '../../context/contexts/message';
import { useReporting } from '../../context/contexts/reporting';
import { selectRecipientInfo, useStaticContext } from '../../context/contexts/static';
import { getDismissOfferMoreGigsEvent } from '../OfferMoreGigs/helper';
import { useRemoteDate } from '../../hooks';

export const ContactSellerPopUp = ({
    onOpen = noop,
    drawerView,
    onClose = noop,
    beforeOpen = noop,
    className,
    isOpen,
    onSubmitSuccess,
    onSubmitFailure,
    overlayClassName,
    confirmationModalOverlayClassName,
    Component
}) => {
    const [isSent, setSend] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [currentStep, setCurrentStep] = useState(STEPS.MESSAGE_FORM);

    const context = useStaticContext();
    const { utcOffset } = selectRecipientInfo(context);
    const { isNight } = useRemoteDate({ utcOffset });
    const { message, clearMessage } = useMessage();
    const { attachments, clearAttachments } = useAttachments();
    const { sendBiEvent, sendMixpanelEvent } = useReporting();

    const closePopUp = () => {
        currentStep === STEPS.OFFER_MORE_GIGS && sendMixpanelEvent(getDismissOfferMoreGigsEvent());
        onClose();
    };

    const onOpenModal = () => {
        const event = { type: FIRST_MESSAGE_POPUP_IS_SHOWN, params: { action: drawerView ? 'drawer' : '' } };

        sendBiEvent(event);
        sendMixpanelEvent(event);

        onOpen();
    };

    const shouldShowConfirmationMessage = () => {
        const hasMessage = !isEmpty(message);
        const hasAttachments = !isEmpty(attachments);

        return (hasMessage || hasAttachments) && !isSent;
    };

    const onSubmit = () => {
        setSend(true);
        onSubmitSuccess();
    };

    const onFailure = () => {
        StatusToast.raise({
            message: translate('send_message.validation.something_wrong'),
            theme: StatusToast.THEMES.ERROR
        });

        onSubmitFailure();
    };

    const onCloseModal = () => {
        shouldShowConfirmationMessage() ? setShowConfirmationModal(true) : closePopUp();
    };

    const onClosePopover = () => {
        setShowConfirmationModal(false);
        clearMessage();
        clearAttachments();
        closePopUp();
    };

    const onCloseButtonClick = () => {
        const event = {
            type: CLICKED_ON_X_BUTTON,
            params: {
                isSent,
                currentStep
            }
        };

        sendBiEvent(event);
        sendMixpanelEvent(event);
        onCloseModal();
    };

    const onCloseConfirmationModal = ({ isConfirmed }) => {
        setShowConfirmationModal(false);

        if (isConfirmed) {
            clearMessage();
            clearAttachments();
            closePopUp();
        }
    };

    return (
        <Component isNight={isNight}
            isOpen={isOpen}
            isSent={isSent}
            showConfirmationModal={showConfirmationModal}
            className={className}
            overlayClassName={overlayClassName}
            confirmationModalOverlayClassName={confirmationModalOverlayClassName}
            onStepChange={setCurrentStep}
            onOpen={onOpenModal}
            onClose={onCloseModal}
            onSubmit={onSubmit}
            onFailure={onFailure}
            onClosePopover={onClosePopover}
            onCloseButtonClick={onCloseButtonClick}
            onCloseConfirmationModal={onCloseConfirmationModal}
            beforeOpen={beforeOpen}/>
    );
};

ContactSellerPopUp.propTypes = {
    isOpen: PropTypes.bool,
    drawerView: PropTypes.bool,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    beforeOpen: PropTypes.func,
    className: PropTypes.string,
    overlayClassName: PropTypes.string,
    confirmationModalOverlayClassName: PropTypes.string,
    onSubmitSuccess: PropTypes.func.isRequired,
    onSubmitFailure: PropTypes.func.isRequired,
    Component: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.node
    ])
};
